import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mic, MicOff, FileText, Send, Check, Loader2, Upload } from 'lucide-react';
import { consultationsApi, notesApi } from '../lib/api';
import { format } from 'date-fns';
import SOAPNoteEditor from '../components/consultation/SOAPNoteEditor';

export default function ConsultationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [transcript, setTranscript] = useState('');
  const [activeTab, setActiveTab] = useState('transcript'); // transcript | notes
  const [isRecording, setIsRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [generatingNote, setGeneratingNote] = useState(false);
  const [noteType, setNoteType] = useState('soap');
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  const { data: consultation, isLoading } = useQuery({
    queryKey: ['consultation', id],
    queryFn: () => consultationsApi.get(id),
  });

  const { data: notes, refetch: refetchNotes } = useQuery({
    queryKey: ['consultation-notes', id],
    queryFn: () => consultationsApi.notes(id),
  });

  const saveTranscript = useMutation({
    mutationFn: () => consultationsApi.addTranscript(id, transcript),
    onSuccess: () => qc.invalidateQueries(['consultation', id]),
  });

  // --- Recording ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.current.push(e.data);
      };

      mediaRecorder.current.onstop = async () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        const file = new File([blob], 'recording.webm', { type: 'audio/webm' });
        setUploadProgress(0);
        try {
          const result = await consultationsApi.uploadAudio(id, file, (e) => {
            setUploadProgress(Math.round(e.loaded / e.total * 100));
          });
          setTranscript(result.transcript);
          setUploadProgress(null);
          qc.invalidateQueries(['consultation', id]);
        } catch (err) {
          alert('Transcription failed: ' + (err.response?.data?.error || err.message));
          setUploadProgress(null);
        }
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (err) {
      alert('Microphone access denied');
    }
  };

  const stopRecording = () => {
    mediaRecorder.current?.stop();
    setIsRecording(false);
  };

  // --- Generate Note ---
  const handleGenerateNote = async () => {
    if (!transcript && !consultation?.raw_transcript) {
      alert('Please add a transcript first');
      return;
    }

    // Save transcript first if modified
    if (transcript && transcript !== consultation?.raw_transcript) {
      await saveTranscript.mutateAsync();
    }

    setGeneratingNote(true);
    try {
      await consultationsApi.generateNote(id, { noteType });
      await refetchNotes();
      setActiveTab('notes');
    } catch (err) {
      alert('Note generation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingNote(false);
    }
  };

  if (isLoading) return <div className="p-8 text-clinical-muted">Loading…</div>;
  if (!consultation) return <div className="p-8 text-red-600">Consultation not found.</div>;

  const latestSOAP = notes?.find(n => n.note_type === 'soap');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-clinical-border px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="btn-ghost -ml-2">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-semibold text-slate-800">{consultation.patient_name}</h1>
              <span className="text-clinical-muted">·</span>
              <span className="text-sm text-clinical-muted font-mono">{consultation.patient_number}</span>
            </div>
            <p className="text-xs text-clinical-muted mt-0.5">
              {consultation.chief_complaint} · {format(new Date(consultation.consultation_date), 'dd MMM yyyy, HH:mm')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge ${consultation.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}>
            {consultation.status?.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — transcript input */}
        <div className="w-1/2 border-r border-clinical-border flex flex-col">
          <div className="px-6 py-4 border-b border-clinical-border bg-slate-50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Consultation Transcript</h2>
            <div className="flex items-center gap-2">
              {/* Audio recording */}
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={isRecording ? 'btn bg-red-500 text-white hover:bg-red-600' : 'btn-secondary'}
                disabled={!!uploadProgress}
                title={isRecording ? 'Stop recording' : 'Record audio'}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                {isRecording ? 'Stop' : 'Record'}
              </button>

              {/* Save transcript */}
              <button
                onClick={() => saveTranscript.mutate()}
                disabled={saveTranscript.isPending || !transcript}
                className="btn-secondary"
              >
                <Send className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>

          {uploadProgress !== null && (
            <div className="px-6 py-2 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                Transcribing audio… {uploadProgress}%
              </div>
            </div>
          )}

          <textarea
            className="flex-1 resize-none p-6 text-sm text-slate-800 leading-relaxed focus:outline-none font-mono"
            placeholder="Type or paste the consultation notes here, or record audio above to auto-transcribe…"
            value={transcript || consultation.raw_transcript || ''}
            onChange={(e) => setTranscript(e.target.value)}
          />

          {/* Generate note button */}
          <div className="px-6 py-4 border-t border-clinical-border bg-slate-50">
            <div className="flex items-center gap-3">
              <select
                className="input flex-shrink-0 w-auto"
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
              >
                <option value="soap">SOAP Note</option>
                <option value="referral">Referral Letter</option>
                <option value="discharge">Discharge Summary</option>
              </select>
              <button
                onClick={handleGenerateNote}
                disabled={generatingNote}
                className="btn-primary flex-1 justify-center"
              >
                {generatingNote ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                ) : (
                  <><FileText className="w-4 h-4" /> Generate {noteType === 'soap' ? 'SOAP Note' : noteType === 'referral' ? 'Referral' : 'Discharge Summary'}</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right panel — generated notes */}
        <div className="w-1/2 flex flex-col">
          <div className="px-6 py-4 border-b border-clinical-border bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700">Generated Notes</h2>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {!notes?.length ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <FileText className="w-12 h-12 text-slate-200 mb-3" />
                <p className="text-sm text-clinical-muted">No notes generated yet.</p>
                <p className="text-xs text-clinical-muted mt-1">Add a transcript and click Generate.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map(note => (
                  note.note_type === 'soap'
                    ? <SOAPNoteEditor key={note.id} note={note} />
                    : <FreeTextNote key={note.id} note={note} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FreeTextNote({ note }) {
  const qc = useQueryClient();
  const [content, setContent] = useState(note.content?.content || '');
  const [finalising, setFinalising] = useState(false);

  const handleFinalise = async () => {
    setFinalising(true);
    try {
      await notesApi.finalise(note.id);
      qc.invalidateQueries(['consultation-notes']);
    } finally {
      setFinalising(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 capitalize">{note.note_type.replace('_', ' ')}</h3>
        <div className="flex items-center gap-2">
          <span className={`badge ${note.status === 'finalised' ? 'badge-green' : 'badge-yellow'}`}>{note.status}</span>
          {note.status !== 'finalised' && (
            <button onClick={handleFinalise} disabled={finalising} className="btn-primary text-xs py-1 px-2">
              <Check className="w-3 h-3" />
              Finalise
            </button>
          )}
        </div>
      </div>
      <textarea
        className="w-full resize-none text-sm text-slate-800 leading-relaxed font-mono focus:outline-none min-h-[200px]"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        readOnly={note.status === 'finalised'}
      />
    </div>
  );
}
